import { makeAutoObservable, runInAction, flow } from 'mobx';

import { NewVendor, Vendor, VendorModel } from '../models/Vendor';
import { getVendors, updateVendor, addVendor, deleteVendor } from '../services/VendorsAPI';
import { ReferenceData } from '../models/common';
import { errorStore } from './ErrorStore';
import { getActiveDepartmentCode } from '../utils/ActiveDepartment';

class VendorStore {
  data: Vendor[] = [];
  isLoading = false;
  showModal = false;
  currentRow: VendorModel | undefined;

  constructor() {
    makeAutoObservable(this)
  }

  loadData = flow(function* (this: VendorStore) {
    if (this.data.length || this.isLoading) return;
    const departmentCode = getActiveDepartmentCode();

    try {
      this.setLoading(true);
      const response = yield getVendors();
      if (departmentCode !== getActiveDepartmentCode()) {
        return;
      }

      this.setData(response || []);
    } catch (error: any) {
      this.setError(error.message);
    } finally {
      this.setLoading(false);
    }
  });

  async reloadForActiveDepartment() {
    this.reset();
    await this.loadData();
  }

  async delete(vendor: Vendor) {
    try {
      this.setLoading(true);
      await deleteVendor(vendor.id);
      this.setData(this.data.filter(v => v.id !== vendor.id));
      this.clearForm();
    } catch (error: any) {
      this.setError(error.message);
    } finally {
      this.setLoading(false);
    }
  }

  async update(vendor: Vendor) {
    try {
      this.setLoading(true);
      await updateVendor(vendor);
      runInAction(() => {
        const index = this.data.findIndex(v => v.id === vendor.id);
        this.data[index] = { ...vendor };
      });
      this.removeCurrent();
    } catch (error: any) {
      this.setError(error.message);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  addNew = flow(function* (this: VendorStore, newVendor: NewVendor, onSuccess?: () => void) {
    try {
      this.setLoading(true);
      const addedVendor = yield addVendor(newVendor);
      if (addedVendor) {
        this.addRow(addedVendor);
        this.clearForm();
        onSuccess?.();
      }
    } catch (error: any) {
      this.setError(error.message)
    } finally {
      this.setLoading(false);
    }
  })

  setupNew = () => {
    this.setCurrent(new VendorModel({ name: '', isActive: true }));
    this.setShowModal(true);
  }

  get options() {
    return this.data as ReferenceData[];
  }

  get externalInvoiceVendorId() {
    return this.data.find(ev => ev.name === 'No Vendor')?.id;
  }

  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  setError(error: string) {
    errorStore.report(error);
  }

  addRow(vendor: Vendor) {
    this.data = [vendor, ...this.data];
  }

  setShowModal(show: boolean) {
    this.showModal = show;
  }

  removeCurrent() {
    this.currentRow = undefined;
  }

  setCurrent(currentVendor?: VendorModel) {
    this.currentRow = currentVendor;
  }

  clearForm() {
    this.removeCurrent();
    this.setShowModal(false);
  }

  reset() {
    this.data = [];
    this.isLoading = false;
    this.clearForm();
  }

  setData(data: Vendor[]) {
    this.data = data;
  }
}

export const vendorStore = new VendorStore();
